/**
 * Main customization content — ingredient variations, modifier groups,
 * combo sections, special instructions, and quantity selector.
 */
import { useTranslation } from 'react-i18next'
import { Button, Separator, Label, Textarea } from '@/components/ui'
import { isRowVisible } from '@/utils/ruleEngine'
import { formatPrice } from '@/utils/formatting'
import { ItemHeader } from './ItemHeader'
import { ModifierGroup } from './ModifierGroup'
import { ComboMainMeal } from './ComboMainMeal'
import { ComboGroupSelector } from './ComboGroupSelector'
import { QuantitySelector } from './QuantitySelector'
import type { MenuItem, ComboGroup, ComboRow, ComboRowIngredient } from '@/types/menu'
import type { ModifierOption, ModifierGroupVariation } from '@/types/modifier'
import type { SelectedModifier, ComboGroupSelection, IngredientVariationSelection } from '@/types/cart'
import type { RuleEvaluationResult } from '@/types/logicRules'

interface CustomizationContentProps {
  item: MenuItem
  quantity: number
  onQuantityChange: (qty: number) => void
  selectedModifiers: SelectedModifier[]
  setSelectedModifiers: React.Dispatch<React.SetStateAction<SelectedModifier[]>>
  specialInstructions: string
  onSpecialInstructionsChange: (value: string) => void
  onModifierToggle: (groupId: string, groupName: string, optionId: string, optionName: string, price: number, variationId?: string, variationName?: string) => void
  onQuantityModifierChange: (groupId: string, groupName: string, optionId: string, optionName: string, price: number, newQty: number) => void
  isIngredientEnabled: (groupId: string, optionId: string) => boolean
  onOpenVariationPicker: (groupId: string, groupName: string, option: ModifierOption) => void
  singleItemRuleResult: RuleEvaluationResult
  getGroupOptionsToDisplay: (group: { id: string; options: ModifierOption[]; variations?: ModifierGroupVariation[] }) => ModifierOption[]
  getRowIngredientsToDisplay: (row: ComboRow) => ComboRowIngredient[]
  mainMealCustomizations: SelectedModifier[]
  setMainMealCustomizations: React.Dispatch<React.SetStateAction<SelectedModifier[]>>
  comboGroupSelections: ComboGroupSelection[]
  onComboGroupToggle: (group: ComboGroup, optionId: string) => void
  ingredientVariationSelections: IngredientVariationSelection[]
  setIngredientVariationSelections: React.Dispatch<React.SetStateAction<IngredientVariationSelection[]>>
  mainMealRuleResult: RuleEvaluationResult
  mainMealExpanded: boolean
  setMainMealExpanded: (expanded: boolean) => void
  groupRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>
  getSelectionLimit: (groupId: string) => number
}

export function CustomizationContent({
  item,
  quantity,
  onQuantityChange,
  selectedModifiers,
  setSelectedModifiers,
  specialInstructions,
  onSpecialInstructionsChange,
  onModifierToggle,
  onQuantityModifierChange,
  isIngredientEnabled,
  onOpenVariationPicker,
  singleItemRuleResult,
  getGroupOptionsToDisplay,
  getRowIngredientsToDisplay,
  mainMealCustomizations,
  setMainMealCustomizations,
  comboGroupSelections,
  onComboGroupToggle,
  ingredientVariationSelections,
  setIngredientVariationSelections,
  mainMealRuleResult,
  mainMealExpanded,
  setMainMealExpanded,
  groupRefs,
  getSelectionLimit,
}: CustomizationContentProps) {
  const { t } = useTranslation()

  const hasComboProduct = item.itemType === 'combo' && item.comboProduct
  const mainMeal = item.comboProduct?.mainMeal
  const comboGroups = item.comboProduct?.groups || []

  return (
    <>
      {/* Hero image */}
      {item.image_url && (
        <div className="relative w-full aspect-[16/10] bg-muted overflow-hidden">
          <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        </div>
      )}

      <div className={`relative px-5 sm:px-6 pb-6 ${item.image_url ? '-mt-8 pt-4' : 'pt-16'}`}>
        <ItemHeader item={item} />

        {/* Ingredient Variations (size, etc.) */}
        {item.ingredientVariationGroup && (
          <>
            <Separator className="my-5" />
            <div className="space-y-3">
              <Label className="text-base font-semibold">{item.ingredientVariationGroup.name}</Label>
              <div className="flex flex-wrap gap-2">
                {(() => {
                  const variations = item.ingredientVariationGroup.variations
                    .slice()
                    .sort((a, b) => a.sort_order - b.sort_order)

                  const defaultVariation = variations.find((v) => v.is_default)
                  const defaultPrice = defaultVariation?.price ?? item.price

                  return variations.map((v) => {
                    const isSelected = selectedModifiers.some(
                      (m) => m.groupId === item.ingredientVariationGroup!.id && m.optionId === v.id
                    )

                    let priceLabel = ''
                    if (v.price !== undefined && defaultPrice !== undefined) {
                      const diff = v.price - defaultPrice
                      if (diff > 0) {
                        priceLabel = ` (+${formatPrice(diff, item.currency, false)})`
                      } else if (diff < 0) {
                        priceLabel = ` (-${formatPrice(Math.abs(diff), item.currency, false)})`
                      }
                    }

                    return (
                      <Button
                        key={v.id}
                        variant={isSelected ? 'default' : 'secondary'}
                        className="px-4 py-2.5 h-auto rounded-full text-sm"
                        onClick={() => {
                          setSelectedModifiers((prev) => [
                            ...prev.filter((m) => m.groupId !== item.ingredientVariationGroup!.id),
                            {
                              groupId: item.ingredientVariationGroup!.id,
                              groupName: item.ingredientVariationGroup!.name,
                              optionId: v.id,
                              optionName: v.name,
                              priceAdjustment: 0,
                            },
                          ])
                        }}
                      >
                        {v.name}{priceLabel}
                      </Button>
                    )
                  })
                })()}
              </div>
            </div>
          </>
        )}

        <Separator className="my-5" />

        {/* Single Item: Modifiers */}
        {item.itemType === 'single' && item.modifierGroups && item.modifierGroups.length > 0 && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">{t('common:customizeYourItem')}</h3>
            {item.modifierGroups
              .filter((group) => isRowVisible(group.id, singleItemRuleResult, true))
              .map((group) => {
                const displayOptions = getGroupOptionsToDisplay(group)
                const selectedInGroup = selectedModifiers.filter((m) => m.groupId === group.id)
                const maxSelections = getSelectionLimit(group.id)

                return (
                  <div key={group.id} ref={(el) => { groupRefs.current[group.id] = el }}>
                    <ModifierGroup
                      groupId={group.id}
                      groupName={group.name}
                      selectionType={group.selectionType}
                      options={displayOptions}
                      selectedOptions={selectedInGroup}
                      maxSelections={maxSelections}
                      minSelections={group.minSelections}
                      maxQuantityPerItem={group.maxQuantityPerItem}
                      onToggle={onModifierToggle}
                      onQuantityChange={onQuantityModifierChange}
                      isIngredientEnabled={isIngredientEnabled}
                      onOpenVariationPicker={onOpenVariationPicker}
                      menuItem={item}
                    />
                  </div>
                )
              })}
          </div>
        )}

        {/* Combo Item: Main Meal + Groups */}
        {hasComboProduct && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">{t('common:buildYourCombo')}</h3>
            {mainMeal && (
              <ComboMainMeal
                mainMeal={mainMeal}
                customizations={mainMealCustomizations}
                onCustomizationChange={setMainMealCustomizations}
                ingredientVariationSelections={ingredientVariationSelections}
                onIngredientVariationChange={setIngredientVariationSelections}
                ruleResult={mainMealRuleResult}
                getIngredientsToDisplay={getRowIngredientsToDisplay}
                expanded={mainMealExpanded}
                onExpandedChange={setMainMealExpanded}
                menuItem={item}
              />
            )}
            {comboGroups.length > 0 && (
              <div className="space-y-6">
                {[...comboGroups]
                  .sort((a, b) => a.order - b.order)
                  .map((group) => (
                    <div key={group.id} ref={(el) => { groupRefs.current[group.id] = el }}>
                      <ComboGroupSelector
                        group={group}
                        selectedOptions={comboGroupSelections.filter((s) => s.groupId === group.id)}
                        onToggle={onComboGroupToggle}
                      />
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        <Separator className="my-5" />

        {/* Special Instructions */}
        <div className="space-y-3">
          <Label htmlFor="instructions" className="text-base font-semibold">
            {t('common:specialInstructions')}
          </Label>
          <Textarea
            id="instructions"
            placeholder={t('common:specialInstructionsPlaceholder')}
            value={specialInstructions}
            onChange={(e) => onSpecialInstructionsChange(e.target.value)}
            rows={3}
          />
        </div>

        <Separator className="my-5" />

        {/* Quantity Selector */}
        <QuantitySelector quantity={quantity} onQuantityChange={onQuantityChange} />
      </div>
    </>
  )
}
